/**
 * Persian date and digit formatting.
 *
 * Shared by the static site generator and the browser widget, which is why it
 * lives here rather than in `src/site/`. It is pure, dependency-free and
 * deterministic: the same ISO string produces the same Persian string on every
 * machine, in the browser and in Node, because the Jalali conversion is
 * arithmetic rather than `Intl`. A date that depends on the runtime's ICU data
 * is a date that can change between two renders of the same content.
 *
 * Tamoomeh-owned, not vendored from Tamoomeh.
 */

const PERSIAN_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] as const;

/** Convert Latin digits to Persian digits for display. */
export function toPersianDigits(value: string | number): string {
  return String(value).replace(/[0-9]/g, (digit) => PERSIAN_DIGITS[Number(digit)] ?? digit);
}

/**
 * Gregorian → Jalali conversion (Birashk-style arithmetic).
 *
 * Implemented here rather than through `Intl` so the rendered date is identical
 * on every machine and every Node build.
 */
export function gregorianToJalali(gy: number, gm: number, gd: number): [number, number, number] {
  const gDaysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let gy2 = gm > 2 ? gy + 1 : gy;
  let days =
    355666 +
    365 * gy +
    Math.floor((gy2 + 3) / 4) -
    Math.floor((gy2 + 99) / 100) +
    Math.floor((gy2 + 399) / 400) +
    gd +
    gDaysInMonth.slice(0, gm - 1).reduce((sum, value) => sum + value, 0);
  let jy = -1595 + 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return [jy, jm, jd];
}

/** `2026-09-22` → `۳۱ شهریور ۱۴۰۵`. */
export function formatPersianDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return toPersianDigits(iso);
  const [, year, month, day] = match;
  const [jy, jm, jd] = gregorianToJalali(Number(year), Number(month), Number(day));
  return `${toPersianDigits(jd)} ${PERSIAN_MONTHS[jm - 1]} ${toPersianDigits(jy)}`;
}

/**
 * An ISO-8601 instant → `۳۱ شهریور ۱۴۰۵، ساعت ۱۴:۰۵`.
 *
 * The clock is rendered in the visitor's own timezone, because "when was this
 * created" is a question about their wall clock. The date part is Jalali.
 */
export function formatPersianDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return toPersianDigits(iso);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${formatPersianDate(iso)}، ساعت ${toPersianDigits(hours)}:${toPersianDigits(minutes)}`;
}
